/* eslint-disable react-hooks/set-state-in-effect */
import React, { useCallback, useEffect, useRef, useState } from "react";

import { useQueryClient } from "@tanstack/react-query";

import { PrototypeChatPanel } from "./PrototypeChatPanel";
import { PrototypePreviewPanel } from "./PrototypePreviewPanel";
import {
  getExperimentApiErrorMessage,
  getPrototypeDetailQueryKey,
  mergePrototypeDetailWithBuildResponse,
  resolvePrototypeIterationIdFromDetail,
  useCreatePrototype,
  usePrototypeByExperiment,
  usePrototypeStatus,
  useUpdatePrototype,
} from "../../api/experimentsApi";
import { useExperimentEditor } from "../../hooks/useExperimentEditor";
import { formatExperimentStatusLabel } from "../../utils/experimentStatus";

import type { ChatMessage } from "./PrototypeChatPanel";
import type { PreviewMode } from "./PrototypePreviewPanel";
import type {
  CreatePrototypeResponse,
  PrototypeDetailResponse,
  PrototypeStatusResponse,
} from "../../api/experimentsApi";
import type { ExperimentFormData } from "../../types/experiment.types";

interface ExperimentPrototypeWorkspaceProps {
  experimentData: ExperimentFormData;
  setExperimentData: React.Dispatch<React.SetStateAction<ExperimentFormData>>;
  onSaveAndExit: () => void;
}

type PrototypeJobLifecycle =
  | "idle"
  | "submitting"
  | "polling"
  | "completed"
  | "failed"
  | "timed_out";

interface PrototypeJobSession {
  lifecycle: PrototypeJobLifecycle;
  createResponse: CreatePrototypeResponse | null;
  latestStatus: PrototypeStatusResponse | null;
  experimentId: string | null;
  iterationId: string | null;
  baseUrl: string | null;
  previewUrl: string | null;
  trackingUrl: string | null;
  error: string | null;
  startedAt: number | null;
}

const PROTOTYPE_TIMEOUT_MS = 10 * 60 * 1000;
const PROTOTYPE_SESSION_STORAGE_KEY =
  "falcon-innovation-studio:prototype-session";

const getPrototypeSessionStorageKey = (experimentId: string) =>
  `${PROTOTYPE_SESSION_STORAGE_KEY}:${experimentId}`;

const createMessage = (
  role: ChatMessage["role"],
  content: string,
): ChatMessage => ({
  id: crypto.randomUUID(),
  role,
  content,
});

const createEmptyPrototypeJobSession = (): PrototypeJobSession => ({
  lifecycle: "idle",
  createResponse: null,
  latestStatus: null,
  experimentId: null,
  iterationId: null,
  baseUrl: null,
  previewUrl: null,
  trackingUrl: null,
  error: null,
  startedAt: null,
});

const readStoredPrototypeSession = (experimentId?: string) => {
  if (!experimentId || typeof window === "undefined") {
    return null;
  }

  try {
    const rawValue = window.localStorage.getItem(
      getPrototypeSessionStorageKey(experimentId),
    );

    if (!rawValue) {
      return null;
    }

    return JSON.parse(rawValue) as {
      hasTriggeredImplement?: boolean;
      planDraft?: string;
      prototypeJob?: PrototypeJobSession | null;
    };
  } catch {
    return null;
  }
};

const writeStoredPrototypeSession = (
  experimentId: string | undefined,
  value: {
    hasTriggeredImplement: boolean;
    planDraft: string;
    prototypeJob: PrototypeJobSession;
  } | null,
) => {
  if (!experimentId || typeof window === "undefined") {
    return;
  }

  try {
    const storageKey = getPrototypeSessionStorageKey(experimentId);
    if (!value) {
      window.localStorage.removeItem(storageKey);
      return;
    }

    window.localStorage.setItem(storageKey, JSON.stringify(value));
  } catch {
    // Ignore storage failures and fall back to in-memory state.
  }
};

const normalizePrototypeStatus = (status?: string | number) => {
  if (typeof status === "number") {
    switch (status) {
      case 0:
        return "queued";
      case 1:
        return "processing";
      case 2:
        return "completed";
      case 3:
        return "failed";
      default:
        return String(status).trim().toLowerCase();
    }
  }

  return (status ?? "").trim().toLowerCase();
};

const resolvePrototypePreviewUrl = (data?: {
  v0WebUrl?: string;
  v0DemoUrl?: string;
  currentVersion?: { demoUrl?: string };
  versions?: Array<{ v0DemoUrl?: string; isCurrentVersion?: boolean }>;
}) => {
  const currentVersionPreview = data?.versions?.find(
    (version) => version.isCurrentVersion,
  )?.v0DemoUrl;
  const urlCandidates = [
    data?.v0DemoUrl,
    currentVersionPreview,
    data?.currentVersion?.demoUrl,
    data?.v0WebUrl,
  ];

  return urlCandidates.find((value) => value?.trim()) ?? null;
};

const resolveApiUrl = (value?: string | null, baseUrl?: string | null) => {
  const trimmedValue = value?.trim();

  if (!trimmedValue) {
    return null;
  }

  try {
    return baseUrl ? new URL(trimmedValue, baseUrl).toString() : trimmedValue;
  } catch {
    return trimmedValue;
  }
};

const getStatusAnnouncement = (status: string) => {
  switch (normalizePrototypeStatus(status)) {
    case "queued":
    case "pending":
      return "Prototype build accepted. The job is queued and I’m checking status every 10 seconds.";
    case "processing":
      return "Prototype build is now processing. I’ll switch the preview as soon as the generated app is ready.";
    default:
      return null;
  }
};

const formatGenerationStatus = (status?: string | number) => {
  const normalizedStatus = normalizePrototypeStatus(status);

  switch (normalizedStatus) {
    case "queued":
      return "Queued";
    case "pending":
      return "Pending";
    case "processing":
      return "Processing";
    case "completed":
      return "Completed";
    case "failed":
      return "Failed";
    case "submitting":
      return "Submitting";
    default:
      return normalizedStatus
        ? normalizedStatus.charAt(0).toUpperCase() + normalizedStatus.slice(1)
        : "Awaiting plan";
  }
};

const formatSummaryPlanMessage = (summaryPlan?: string | null) => {
  const trimmedSummaryPlan = summaryPlan?.trim();

  if (!trimmedSummaryPlan) {
    return "Loaded the existing implementation plan for this experiment.";
  }

  return `Summary plan\n\n${trimmedSummaryPlan}`;
};

const restorePrototypeSession = (
  prototype: PrototypeDetailResponse,
  baseUrl: string | null,
): PrototypeJobSession => {
  const previewUrl = resolvePrototypePreviewUrl(prototype);
  const normalizedStatus = normalizePrototypeStatus(prototype.status);
  const iterationId = resolvePrototypeIterationIdFromDetail(prototype);
  const lifecycle: PrototypeJobLifecycle =
    normalizedStatus === "failed" || prototype.errorMessage?.trim()
      ? "failed"
      : normalizedStatus === "completed" && previewUrl
        ? "completed"
        : iterationId
          ? "polling"
          : "idle";

  return {
    lifecycle,
    createResponse: {
      experimentId: prototype.experimentId?.trim(),
      iterationId: iterationId ?? undefined,
      status: normalizedStatus || prototype.status,
      v0WebUrl: prototype.v0WebUrl,
    },
    latestStatus: null,
    experimentId: prototype.experimentId?.trim() || null,
    iterationId,
    baseUrl,
    previewUrl,
    trackingUrl: null,
    error: prototype.errorMessage?.trim() || null,
    startedAt: lifecycle === "polling" ? Date.now() : null,
  };
};

export const ExperimentPrototypeWorkspace: React.FC<
  ExperimentPrototypeWorkspaceProps
> = ({
  experimentData,
  setExperimentData: _setExperimentData,
  onSaveAndExit: _onSaveAndExit,
}) => {
  const {
    experimentStatus,
    hasPendingOrchestrationChanges,
    isExperimentViewOnly,
    orchestrationId,
    orchestrationPlan,
    orchestrationProgress,
    orchestrationStatus,
    orchestrationError,
    isOrchestrationLoading,
    runOrchestration,
  } = useExperimentEditor();
  const [leftPanelWidth, setLeftPanelWidth] = useState(400);
  const [planDraft, setPlanDraft] = useState("");
  const [planMessages, setPlanMessages] = useState<ChatMessage[]>([]);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [hasTriggeredImplement, setHasTriggeredImplement] = useState(false);
  const [prototypeJob, setPrototypeJob] = useState<PrototypeJobSession>(
    createEmptyPrototypeJobSession,
  );
  const queryClient = useQueryClient();
  const createPrototypeMutation = useCreatePrototype();
  const updatePrototypeMutation = useUpdatePrototype();
  const lastAnnouncedStatusRef = useRef<string | null>(null);
  const restoredPrototypeIdRef = useRef<string | null>(null);
  const experimentId =
    experimentData?.experimentId?.trim?.() || experimentData?.experimentId;
  const readOnlyMessage = React.useMemo(
    () =>
      `This experiment is ${formatExperimentStatusLabel(
        experimentStatus,
      ).toLowerCase()}. Prototype changes are disabled.`,
    [experimentStatus],
  );

  const existingPrototypeQuery = usePrototypeByExperiment(experimentId, {
    enabled:
      Boolean(experimentId) &&
      !hasTriggeredImplement &&
      !hasPendingOrchestrationChanges &&
      !isOrchestrationLoading &&
      !orchestrationId &&
      !orchestrationPlan?.trim(),
  });

  useEffect(() => {
    restoredPrototypeIdRef.current = null;
  }, [experimentId]);

  useEffect(() => {
    if (!experimentId || hasPendingOrchestrationChanges) {
      return;
    }

    const storedSession = readStoredPrototypeSession(experimentId);
    const storedJob = storedSession?.prototypeJob;

    if (!storedSession?.hasTriggeredImplement || !storedJob?.iterationId) {
      return;
    }

    setPlanDraft(storedSession.planDraft?.trim() || "");
    setHasTriggeredImplement(true);
    setPrototypeJob(storedJob);
    lastAnnouncedStatusRef.current =
      storedJob.lifecycle === "completed" || storedJob.lifecycle === "failed"
        ? storedJob.lifecycle
        : null;
  }, [experimentId, hasPendingOrchestrationChanges]);

  useEffect(() => {
    if (!experimentId) {
      return;
    }

    const shouldPersist =
      hasTriggeredImplement &&
      Boolean(prototypeJob.experimentId) &&
      Boolean(prototypeJob.iterationId);

    writeStoredPrototypeSession(
      experimentId,
      shouldPersist
        ? {
            hasTriggeredImplement,
            planDraft,
            prototypeJob,
          }
        : null,
    );
  }, [experimentId, hasTriggeredImplement, planDraft, prototypeJob]);

  useEffect(() => {
    if (!hasPendingOrchestrationChanges) {
      return;
    }

    setPlanDraft("");
    setPlanMessages([]);
    setChatMessages([]);
    setHasTriggeredImplement(false);
    setPrototypeJob(createEmptyPrototypeJobSession());
    lastAnnouncedStatusRef.current = null;
    restoredPrototypeIdRef.current = null;
    writeStoredPrototypeSession(experimentId, null);
  }, [experimentId, hasPendingOrchestrationChanges]);

  useEffect(() => {
    if (orchestrationPlan?.trim()) {
      setPlanDraft(orchestrationPlan.trim());
    }
  }, [orchestrationPlan]);

  useEffect(() => {
    if (
      !experimentId ||
      !hasPendingOrchestrationChanges ||
      orchestrationId ||
      isOrchestrationLoading ||
      existingPrototypeQuery.isLoading
    ) {
      return;
    }

    void runOrchestration(experimentId);
  }, [
    experimentId,
    existingPrototypeQuery.data,
    existingPrototypeQuery.isLoading,
    hasPendingOrchestrationChanges,
    isOrchestrationLoading,
    orchestrationId,
    runOrchestration,
  ]);

  const appendAssistantMessage = useCallback((content: string) => {
    setChatMessages((prev) => [...prev, createMessage("assistant", content)]);
  }, []);

  const retryPlanGeneration = useCallback(() => {
    if (isExperimentViewOnly || !experimentId) {
      return;
    }

    void runOrchestration(experimentId, { force: true });
  }, [experimentId, isExperimentViewOnly, runOrchestration]);

  const setPrototypeFailure = useCallback(
    (
      message: string,
      lifecycle: Extract<
        PrototypeJobLifecycle,
        "failed" | "timed_out"
      > = "failed",
    ) => {
      let didTransition = false;

      setPrototypeJob((prev) => {
        if (prev.lifecycle === "failed" || prev.lifecycle === "timed_out") {
          return prev;
        }

        didTransition = true;

        return {
          ...prev,
          lifecycle,
          error: message,
          previewUrl: prev.previewUrl,
        };
      });

      if (didTransition) {
        appendAssistantMessage(`Prototype generation failed: ${message}`);
      }
    },
    [appendAssistantMessage],
  );

  const isPollingEnabled =
    prototypeJob.lifecycle === "polling" &&
    Boolean(prototypeJob.experimentId) &&
    Boolean(prototypeJob.iterationId);
  const prototypeStatusQuery = usePrototypeStatus(
    prototypeJob.experimentId ?? undefined,
    prototypeJob.iterationId ?? undefined,
    {
      enabled: isPollingEnabled,
      baseUrl: prototypeJob.baseUrl ?? undefined,
    },
  );

  useEffect(() => {
    if (!prototypeStatusQuery.data) return;

    const statusResponse = prototypeStatusQuery.data;
    const normalizedStatus = normalizePrototypeStatus(statusResponse.status);
    const announcedStatus = normalizedStatus || null;
    const previewUrl = resolvePrototypePreviewUrl(statusResponse);

    setPrototypeJob((prev) => ({
      ...prev,
      latestStatus: statusResponse,
      previewUrl:
        normalizedStatus === "completed" && previewUrl
          ? previewUrl
          : prev.previewUrl,
      lifecycle:
        normalizedStatus === "completed" && previewUrl
          ? "completed"
          : prev.lifecycle,
      error: normalizedStatus === "completed" && previewUrl ? null : prev.error,
    }));

    if (
      announcedStatus &&
      announcedStatus !== "completed" &&
      announcedStatus !== "failed" &&
      lastAnnouncedStatusRef.current !== announcedStatus
    ) {
      const announcement = getStatusAnnouncement(announcedStatus);

      if (announcement) {
        appendAssistantMessage(announcement);
      }

      lastAnnouncedStatusRef.current = announcedStatus;
    }

    if (normalizedStatus === "failed" || statusResponse.errorMessage?.trim()) {
      const errorMessage =
        statusResponse.errorMessage?.trim() ||
        "Prototype generation failed before the preview URL became available.";
      setPrototypeFailure(errorMessage, "failed");
      lastAnnouncedStatusRef.current = "failed";
      return;
    }

    if (normalizedStatus === "completed") {
      if (!previewUrl) {
        setPrototypeFailure(
          "Prototype build completed, but no preview URL was returned.",
          "failed",
        );
        lastAnnouncedStatusRef.current = "failed";
        return;
      }

      if (lastAnnouncedStatusRef.current !== "completed") {
        appendAssistantMessage(
          "Prototype generation completed. The preview panel is now showing the generated prototype.",
        );
        const statusExperimentId =
          statusResponse.experimentId?.trim() ||
          prototypeJob.experimentId ||
          experimentId;

        if (statusExperimentId) {
          queryClient.setQueryData<PrototypeDetailResponse | null>(
            getPrototypeDetailQueryKey(statusExperimentId),
            (previous) =>
              mergePrototypeDetailWithBuildResponse(
                previous,
                statusResponse,
                statusExperimentId,
              ),
          );
          void queryClient.invalidateQueries({
            queryKey: getPrototypeDetailQueryKey(statusExperimentId),
            refetchType: "none",
          });
        }
        lastAnnouncedStatusRef.current = "completed";
      }
    }
  }, [
    appendAssistantMessage,
    experimentId,
    prototypeJob.experimentId,
    prototypeStatusQuery.data,
    queryClient,
    setPrototypeFailure,
  ]);

  useEffect(() => {
    if (
      !existingPrototypeQuery.data ||
      !experimentId ||
      hasTriggeredImplement ||
      hasPendingOrchestrationChanges ||
      isOrchestrationLoading
    ) {
      return;
    }

    const restoredPrototype = existingPrototypeQuery.data;
    const restoredPrototypeId = restoredPrototype.id?.trim();

    if (
      !restoredPrototypeId ||
      restoredPrototypeIdRef.current === restoredPrototypeId
    ) {
      return;
    }

    restoredPrototypeIdRef.current = restoredPrototypeId;
    setPlanDraft(restoredPrototype.summaryPlan?.trim() || "");
    setPlanMessages([]);
    setChatMessages([
      createMessage(
        "assistant",
        formatSummaryPlanMessage(restoredPrototype.summaryPlan),
      ),
    ]);
    setHasTriggeredImplement(true);
    setPrototypeJob(restorePrototypeSession(restoredPrototype, null));

    const normalizedStatus = normalizePrototypeStatus(restoredPrototype.status);
    lastAnnouncedStatusRef.current =
      normalizedStatus === "completed" || normalizedStatus === "failed"
        ? normalizedStatus
        : null;
  }, [
    existingPrototypeQuery.data,
    experimentId,
    hasPendingOrchestrationChanges,
    hasTriggeredImplement,
    isOrchestrationLoading,
  ]);

  useEffect(() => {
    if (!existingPrototypeQuery.error) return;

    setPlanMessages((prev) => {
      if (
        prev.some(
          (message) =>
            message.role === "assistant" &&
            message.content ===
              "Unable to load the existing prototype for this experiment.",
        )
      ) {
        return prev;
      }

      return [
        ...prev,
        createMessage(
          "assistant",
          "Unable to load the existing prototype for this experiment.",
        ),
      ];
    });
  }, [existingPrototypeQuery.error]);

  useEffect(() => {
    if (!prototypeStatusQuery.error) return;

    setPrototypeFailure(
      getExperimentApiErrorMessage(
        prototypeStatusQuery.error,
        "Unable to load prototype status.",
      ),
      "failed",
    );
    lastAnnouncedStatusRef.current = "failed";
  }, [prototypeStatusQuery.error, setPrototypeFailure]);

  useEffect(() => {
    if (
      !prototypeJob.startedAt ||
      (prototypeJob.lifecycle !== "submitting" &&
        prototypeJob.lifecycle !== "polling")
    ) {
      return;
    }

    const remainingMs =
      PROTOTYPE_TIMEOUT_MS - (Date.now() - prototypeJob.startedAt);

    if (remainingMs <= 0) {
      setPrototypeFailure(
        "Prototype generation timed out after 10 minutes.",
        "timed_out",
      );
      lastAnnouncedStatusRef.current = "timed_out";
      return;
    }

    const timeoutId = window.setTimeout(() => {
      setPrototypeFailure(
        "Prototype generation timed out after 10 minutes.",
        "timed_out",
      );
      lastAnnouncedStatusRef.current = "timed_out";
    }, remainingMs);

    return () => window.clearTimeout(timeoutId);
  }, [prototypeJob.lifecycle, prototypeJob.startedAt, setPrototypeFailure]);

  const handleDrag = (e: React.MouseEvent) => {
    e.preventDefault();
    const startX = e.pageX;
    const startWidth = leftPanelWidth;

    const onMouseMove = (moveEvent: MouseEvent) => {
      const newWidth = Math.max(
        300,
        Math.min(800, startWidth + moveEvent.pageX - startX),
      );
      setLeftPanelWidth(newWidth);
    };

    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      document.body.style.cursor = "default";
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    document.body.style.cursor = "col-resize";
  };

  const applyPrototypeMutationResult = ({
    result,
    fallbackExperimentId,
    preservePreview,
  }: {
    result: { data: CreatePrototypeResponse; baseUrl: string };
    fallbackExperimentId: string;
    preservePreview?: boolean;
  }) => {
    const statusExperimentId =
      result.data.experimentId?.trim() || fallbackExperimentId;
    const statusIterationId = result.data.iterationId?.trim();

    if (!statusExperimentId) {
      throw new Error(
        "Prototype generation response did not include an experiment ID.",
      );
    }

    if (!statusIterationId) {
      throw new Error(
        "Prototype generation response did not include an iteration ID.",
      );
    }

    const normalizedResponseStatus = normalizePrototypeStatus(
      result.data.status,
    );
    const responseAnnouncement = getStatusAnnouncement(
      normalizedResponseStatus,
    );
    const immediatePreviewUrl = resolvePrototypePreviewUrl(result.data);

    setPrototypeJob((prev) => ({
      ...prev,
      lifecycle: "polling",
      createResponse: result.data,
      latestStatus: null,
      experimentId: statusExperimentId,
      iterationId: statusIterationId,
      baseUrl: result.baseUrl,
      previewUrl:
        immediatePreviewUrl || (preservePreview ? prev.previewUrl : null),
      trackingUrl: resolveApiUrl(result.data.trackingUrl, result.baseUrl),
      error: null,
      startedAt: Date.now(),
    }));

    if (result.data.message?.trim()) {
      appendAssistantMessage(result.data.message.trim());
    }

    if (responseAnnouncement) {
      appendAssistantMessage(responseAnnouncement);
      lastAnnouncedStatusRef.current = normalizedResponseStatus;
      return;
    }

    appendAssistantMessage(
      "Prototype request submitted. I’ll keep polling the build status until the preview is ready.",
    );
  };

  const submitPrototypeGeneration = async (options?: { isRetry?: boolean }) => {
    if (isExperimentViewOnly) {
      appendAssistantMessage(readOnlyMessage);
      return;
    }

    if (!experimentId) {
      setPlanMessages((prev) => [
        ...prev,
        createMessage(
          "assistant",
          "Experiment ID is missing. Complete Step 1 and save the experiment before implementing the plan.",
        ),
      ]);
      return;
    }

    const statusMessage = options?.isRetry
      ? "Retrying prototype generation. I’m submitting a fresh build request and resetting the preview."
      : "Implement Plan triggered. I’m submitting the prototype build request now.";

    setHasTriggeredImplement(true);
    restoredPrototypeIdRef.current = null;
    setPrototypeJob({
      ...createEmptyPrototypeJobSession(),
      lifecycle: "submitting",
      startedAt: Date.now(),
    });
    lastAnnouncedStatusRef.current = null;
    setChatMessages((prev) => [
      ...prev,
      createMessage("assistant", statusMessage),
    ]);

    try {
      const result = await createPrototypeMutation.mutateAsync({
        experimentId,
        plan: planDraft,
      });
      applyPrototypeMutationResult({
        result,
        fallbackExperimentId: experimentId.trim(),
      });
    } catch (error) {
      setPrototypeFailure(
        getExperimentApiErrorMessage(
          error,
          "Unable to start prototype generation.",
        ),
        "failed",
      );
      lastAnnouncedStatusRef.current = "failed";
    }
  };

  const submitPrototypeUpdate = async ({
    experimentId,
    iterationId,
    message,
    userMessage,
  }: {
    experimentId: string;
    iterationId: string;
    message: string;
    userMessage: ChatMessage;
  }) => {
    if (isExperimentViewOnly) {
      appendAssistantMessage(readOnlyMessage);
      return;
    }

    setChatMessages((prev) => [...prev, userMessage]);
    setPrototypeJob((prev) => ({
      ...prev,
      lifecycle: "submitting",
      error: null,
      startedAt: Date.now(),
    }));
    lastAnnouncedStatusRef.current = null;

    try {
      const result = await updatePrototypeMutation.mutateAsync({
        experimentId,
        payload: {
          iterationId,
          message,
        },
      });

      applyPrototypeMutationResult({
        result,
        fallbackExperimentId: experimentId,
        preservePreview: true,
      });
    } catch (error) {
      setPrototypeFailure(
        getExperimentApiErrorMessage(
          error,
          "Unable to update the prototype with your latest prompt.",
        ),
        "failed",
      );
      lastAnnouncedStatusRef.current = "failed";
    }
  };

  const handleSend = () => {
    if (isExperimentViewOnly) {
      return;
    }

    const trimmedInput = input.trim();
    if (!trimmedInput) return;

    const userMessage = createMessage("user", trimmedInput);
    setInput("");

    if (!hasTriggeredImplement) {
      setPlanDraft(
        (prev) => `${prev}\n\nPlan revision request:\n- ${trimmedInput}`,
      );
      setPlanMessages((prev) => [
        ...prev,
        userMessage,
        createMessage(
          "assistant",
          "Plan updated. Review it and click Implement Plan when you're ready.",
        ),
      ]);
      return;
    }

    if (
      prototypeJob.lifecycle === "submitting" ||
      prototypeJob.lifecycle === "polling"
    ) {
      setChatMessages((prev) => [
        ...prev,
        userMessage,
        createMessage(
          "assistant",
          "A prototype update is already in progress. Wait for it to finish before sending another refinement.",
        ),
      ]);
      return;
    }

    if (!experimentId || !prototypeJob.iterationId) {
      setChatMessages((prev) => [
        ...prev,
        userMessage,
        createMessage(
          "assistant",
          "I need an existing prototype iteration before I can send an update prompt.",
        ),
      ]);
      return;
    }

    void submitPrototypeUpdate({
      experimentId,
      iterationId: prototypeJob.iterationId,
      message: trimmedInput,
      userMessage,
    });
  };

  const generationStatus =
    prototypeJob.lifecycle === "submitting"
      ? "Submitting"
      : prototypeJob.lifecycle === "timed_out"
        ? "Timed out"
        : prototypeJob.lifecycle === "failed"
          ? "Failed"
          : formatGenerationStatus(
              prototypeJob.latestStatus?.status ??
                prototypeJob.createResponse?.status,
            );

  const previewMode: PreviewMode =
    prototypeJob.lifecycle === "failed" ||
    prototypeJob.lifecycle === "timed_out"
      ? "error"
      : prototypeJob.lifecycle === "submitting" ||
          prototypeJob.lifecycle === "polling"
        ? "generating"
        : "ready";

  const isInitialLoading =
    existingPrototypeQuery.isLoading ||
    (isOrchestrationLoading &&
      !orchestrationId &&
      !orchestrationPlan &&
      !existingPrototypeQuery.data);
  const showIdlePreviewLoader =
    !hasTriggeredImplement &&
    !prototypeJob.previewUrl &&
    !existingPrototypeQuery.data &&
    !planDraft.trim() &&
    !orchestrationError &&
    previewMode !== "error";
  const showPlanReadyPreviewState =
    !hasTriggeredImplement &&
    !prototypeJob.previewUrl &&
    !existingPrototypeQuery.data &&
    Boolean(planDraft.trim()) &&
    !orchestrationError &&
    previewMode !== "error";

  const planProgress =
    typeof orchestrationProgress === "number" && orchestrationProgress > 0
      ? orchestrationProgress
      : prototypeJob.lifecycle === "completed"
        ? 100
        : orchestrationProgress;

  const planStage =
    orchestrationStatus?.currentStage ??
    orchestrationStatus?.status ??
    (prototypeJob.lifecycle === "completed" ? "completed" : null);

  return (
    <div className="flex h-full w-full flex-col bg-black pb-4 text-white">
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-4 pt-4">
        <div className="flex min-h-0 w-full flex-1 flex-col overflow-hidden rounded-xl border border-white/10 bg-black shadow-2xl">
          <div className="relative flex min-h-0 flex-1 overflow-hidden">
            <div
              className="relative h-full min-h-0 flex-shrink-0 overflow-hidden"
              style={{ width: `${leftPanelWidth}px` }}
            >
              <div className={isExperimentViewOnly ? "h-full opacity-80" : "h-full"}>
                <PrototypeChatPanel
                  planDraft={planDraft}
                  planMessages={planMessages}
                  chatMessages={chatMessages}
                  input={input}
                  isInitialLoading={isInitialLoading}
                  hasTriggeredImplement={hasTriggeredImplement}
                  isGenerating={
                    prototypeJob.lifecycle === "submitting" ||
                    prototypeJob.lifecycle === "polling"
                  }
                  generationStatus={generationStatus}
                  isSubmittingPlan={
                    createPrototypeMutation.isPending ||
                    updatePrototypeMutation.isPending ||
                    prototypeJob.lifecycle === "submitting"
                  }
                  orchestrationProgress={planProgress}
                  orchestrationStage={planStage}
                  orchestrationStatus={orchestrationStatus?.status ?? null}
                  orchestrationError={orchestrationError}
                  isOrchestrationLoading={isOrchestrationLoading}
                  onPlanDraftChange={setPlanDraft}
                  onInputChange={setInput}
                  onSend={handleSend}
                  onImplementPlan={() => {
                    void submitPrototypeGeneration();
                  }}
                  onRetryPlan={retryPlanGeneration}
                  isReadOnly={isExperimentViewOnly}
                />
              </div>

              {isExperimentViewOnly ? (
                <div className="pointer-events-none absolute left-1/2 top-4 z-20 flex w-[calc(100%-2rem)] -translate-x-1/2 justify-center">
                  <div className="max-w-sm rounded-2xl border border-amber-500/25 bg-[#111111] px-4 py-3 text-sm text-amber-100 shadow-xl">
                    {readOnlyMessage}
                  </div>
                </div>
              ) : null}

              <div
                className="group absolute right-0 top-0 z-10 h-full w-1 translate-x-1/2 cursor-col-resize hover:bg-amber-400/50 active:bg-amber-400"
                onMouseDown={handleDrag}
                role="presentation"
              >
                <div className="absolute top-1/2 -ml-0.5 flex h-8 w-1.5 -translate-y-1/2 flex-col items-center justify-center gap-[2px] rounded-full bg-white/20 group-hover:bg-amber-400/80">
                  <div className="h-1 w-0.5 rounded-full bg-black/40"></div>
                  <div className="h-1 w-0.5 rounded-full bg-black/40"></div>
                </div>
              </div>
            </div>

            <div className="h-full min-w-0 flex-1">
              <PrototypePreviewPanel
                mode={previewMode}
                previewUrl={prototypeJob.previewUrl}
                trackingUrl={prototypeJob.trackingUrl}
                generationStatus={generationStatus}
                generationError={prototypeJob.error}
                isInitialLoading={isInitialLoading}
                showIdleLoader={showIdlePreviewLoader}
                isPlanReady={showPlanReadyPreviewState}
                blockedMessage={orchestrationError}
                onRetry={
                  isExperimentViewOnly
                    ? undefined
                    : () => {
                        void submitPrototypeGeneration({ isRetry: true });
                      }
                }
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
